/* eslint-disable import/order */
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { arrayOf, bool, func, number, shape } from 'prop-types';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, useMediaQuery } from '@mui/material';
import { CancelToken } from 'axios';
import * as R from 'ramda';

// styles
import { SelectorsListStyled } from './selector/Selector.styles';

// Context
import { AuthContext } from 'authentication/AuthContext';

// Components
import Filters from '../../filters/Filters.connector';
import MobileFilters from '../../filters/MobileFilters';
import SelectorSkeleton from './skeleton/Skeleton';
import Selector from './selector/Selector.connector';
import SelectorVirtualizer from '../../selectors-list/SelectorVirtualizer';
import { NoAuthorizationsAvailable } from 'feature/loading-management/shared-components';

// Utils
import { decodeURIWithPct, getSelectionURI } from 'utils/helpers/url';
import { useSetNoAuthCard, useSetSelectorIndex, useURLRouting } from 'hooks';
import { MY_AUTHORIZATIONS_VIEW_BY_OPTIONS, sidebarStyles } from '../../utils/const';
import { useTheme } from '@mui/material/styles';

const Sidebar = ({
  currentSelection,
  fetchMyAuthorizations,
  fetchUniqueSelectors,
  selectedIndex,
  selectorsList,
  selectorsListPending,
  setFilters,
  setSelectorsList,
  setMyAuthorizationPending,
  setMobileDrawerOpen,
}) => {
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });

  const navigate = useNavigate();
  const { viewBy, viewById } = useParams();
  const { pathname } = useLocation();

  const adjustedViewById = decodeURIWithPct(viewById || '');

  const { currentAffiliate } = useContext(AuthContext);
  const fetchUniqueSelectorsToken = useRef();
  const selectorsCancelToken = useRef();
  const previousSelectorFetch = useRef();

  const SELECTOR_SKELETON_HEIGHT = 121; // actual height of the rendered selector container

  const [refetch, setRefetch] = useState(false);

  // this will updated the currentSelection when the user hits the back button
  useURLRouting(currentSelection, selectorsList, adjustedViewById, setFilters);

  // This is to update our selector index based off of the URL
  useSetSelectorIndex(selectorsList, setFilters);

  useSetNoAuthCard(selectorsList, selectorsListPending, setMyAuthorizationPending, refetch);

  useEffect(() => {
    return () => setSelectorsList([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!lgUp && selectedIndex !== null && selectedIndex !== undefined) {
      setMobileDrawerOpen(true);
    } else if (lgUp) {
      setMobileDrawerOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, lgUp]);

  useEffect(() => {
    if (currentAffiliate?.customerNameShort && viewBy) {
      previousSelectorFetch.current = null;
      if (fetchUniqueSelectorsToken.current?.cancel) {
        fetchUniqueSelectorsToken.current.cancel();
        // this bool is so that if we come in and refetch
        // the selectors we don't show the no availaible
        // card in the cards panel
        setRefetch(true);
      }
      const cancelToken = CancelToken.source();
      fetchUniqueSelectorsToken.current = cancelToken;
      // TODO: when using the notifications to route to via click
      // the url has a new affiliate, but we don't push current affiliate
      // via the click in notifications
      fetchUniqueSelectors(
        {
          supplier: {
            companyCustomerIdName: currentAffiliate.customerIdName,
            dataCompanyId: currentAffiliate.dataCompanyId,
          },
        },
        viewBy,
        viewById,
        cancelToken.token
      )
        // Once the latest request resolves, clear refetch regardless of data shape
        // so empty/null responses can still show the No Authorizations card.
        .then(() => setRefetch(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAffiliate?.customerNameShort, currentAffiliate?.dataCompanyId, viewBy]);

  // eslint-disable-next-line consistent-return
  const getParamsObj = useCallback(() => {
    if (selectorsList.length) {
      const optionalParam =
        viewBy === 'inventory-owner'
          ? {
              inventoryOwner: {
                companyCustomerIdName: selectorsList[selectedIndex].companyCustomerIdName,
                dataCompanyId: selectorsList[selectedIndex].dataCompanyId,
              },
            }
          : {
              [`${viewBy}Id`]: selectorsList[selectedIndex][`${viewBy}Id`],
            };

      if (selectorsList[selectedIndex]) {
        return {
          supplier: {
            companyCustomerIdName: currentAffiliate?.customerIdName,
            dataCompanyId: currentAffiliate?.dataCompanyId,
          },
          ...optionalParam,
        };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, selectorsList]);

  useEffect(() => {
    const getNewUrl = () => {
      if (!adjustedViewById) {
        const trimmedPath = pathname.replace(/\/$/, '');
        return `${trimmedPath}/${getSelectionURI(currentSelection)}`;
      }
      return R.replace(viewById, getSelectionURI(currentSelection), pathname);
    };
    if (
      currentSelection?.nameShort &&
      previousSelectorFetch.current !== currentSelection.nameShort
    ) {
      previousSelectorFetch.current = currentSelection.nameShort;
      if (currentSelection?.nameShort !== adjustedViewById) {
        navigate(getNewUrl(), { replace: true });
      }
      if (selectorsCancelToken.current?.cancel) {
        selectorsCancelToken.current.cancel();
      }
      const cancelToken = CancelToken.source();
      selectorsCancelToken.current = cancelToken;
      const params = getParamsObj();
      if (params) fetchMyAuthorizations(params, viewBy, cancelToken.token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelection?.nameShort]);

  return (
    <Box {...sidebarStyles}>
      {!lgUp ? (
        <MobileFilters>
          <Filters
            loading={selectorsListPending}
            viewByOptions={MY_AUTHORIZATIONS_VIEW_BY_OPTIONS}
          />
        </MobileFilters>
      ) : (
        <Filters loading={selectorsListPending} viewByOptions={MY_AUTHORIZATIONS_VIEW_BY_OPTIONS} />
      )}
      {!lgUp && !selectorsListPending && R.isEmpty(selectorsList) ? (
        <Box p={3}>
          <NoAuthorizationsAvailable />
        </Box>
      ) : (
        <SelectorsListStyled
          loading={selectorsListPending}
          selector={
            <SelectorVirtualizer
              itemSize={() => 113}
              overscanCount={4}
              selectorComponent={Selector}
              selectorsData={selectorsList}
            />
          }
          skeletonComponent={<SelectorSkeleton skeletonHeight={SELECTOR_SKELETON_HEIGHT} />}
          skeletonHeight={SELECTOR_SKELETON_HEIGHT}
        />
      )}
    </Box>
  );
};

Sidebar.propTypes = {
  currentSelection: shape({}),
  fetchMyAuthorizations: func.isRequired,
  fetchUniqueSelectors: func.isRequired,
  selectedIndex: number,
  selectorsList: arrayOf(shape({})).isRequired,
  selectorsListPending: bool.isRequired,
  setFilters: func.isRequired,
  setMobileDrawerOpen: func.isRequired,
  setMyAuthorizationPending: func.isRequired,
  setSelectorsList: func.isRequired,
};

Sidebar.defaultProps = {
  currentSelection: {},
  selectedIndex: undefined,
};

export default Sidebar;
